import { parse } from 'mathjs';
import { EPS, valuesMatchWithinEpsilon } from './epsilon.js';
import { buildScopeWithValues, preprocessFormula } from './formula-scope.js';
/** Strips `{name}` chip delimiters to the bare identifier so mathjs's parser sees a plain symbol
 * reference — same preprocessing formula-eval.ts's (and numerical.ts's own port of it) evaluators
 * already do. A literal-only equation (no chips) is a no-op. */
export function stripVariableChips(s) {
    return s.replace(/\{([^}]+)\}/g, '$1');
}
/** Every distinct variable name an equation references, in first-appearance order. */
export function getEquationVariableNames(equation) {
    const names = [];
    /*
     * RECALLS COUNT AS NAMES THIS EQUATION NEEDS.
     *
     * It matched `{variable}` alone, so a `[ara_...]` was not a name the undefined-check could
     * miss — and the equation then failed later, in the parser, returning null with nothing
     * said. A recall is a value this equation cannot be evaluated without, which is exactly
     * what this function is for.
     */
    const re = /\{([^}]+)\}|\[(ara_[0-9a-zA-Z-]+)\]/g;
    let m;
    while ((m = re.exec(equation)) !== null) {
        const name = m[1] ?? m[2];
        if (!names.includes(name))
            names.push(name);
    }
    return names;
}
/** Evaluates `equation` against resolved variable values, returning null if it references a
 * variable with no resolved value, or the expression fails to parse/evaluate to a finite number
 * (mirrors numerical.ts's safeEvalString — same "never throw, just report unresolvable" contract).
 * Evaluates through the same shared scope every formula input in the app uses (formula-scope.ts)
 * — log()/ln(), arcsin-style aliases, |x| notation, and angle-mode-aware trig all behave exactly
 * like they do in a FormulaField's own live preview, so the two can never silently disagree. */
export function evaluateEquation(equation, resolvedVariables, angleMode = 'degrees') {
    const names = getEquationVariableNames(equation);
    if (names.some(n => resolvedVariables[n] === undefined))
        return null;
    try {
        /*
         * ONE REWRITE, SHARED. This stripped `{chips}` and handled abs notation itself and knew
         * nothing of `[ara_...]`, so a branch condition quoting an earlier answer came back null
         * — silently, so no branch matched and nothing said why. Measured before the fix:
         * `evaluateFormulaRaw('2*[ara_x]')` was 10 and this was null, on the same input.
         *
         * That was never one missing line, it was a second evaluator. `preprocessFormula` is the
         * rewrite `evaluateFormulaRaw` and `formulaParses` already share, and this makes three —
         * so what a teacher wrote cannot mean different things on different surfaces.
         */
        const v = parse(preprocessFormula(equation)).evaluate(buildScopeWithValues(angleMode, resolvedVariables));
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
/** `Number('')` (and `Number('   ')`) is `0`, not `NaN` — plain `Number(...)` would silently treat
 * a blank, not-yet-filled-in threshold field as a valid zero. Blank means "not entered," same as
 * any other unparseable text. Exported so the authoring UI can reuse the exact same parsing when
 * checking for an inverted (low > high) 'between' branch, rather than re-deriving it. */
export function parseThreshold(s) {
    return s === undefined || s.trim() === '' ? NaN : Number(s);
}
function branchMatches(value, branch) {
    if (branch.operator === 'between') {
        const lo = parseThreshold(branch.threshold);
        const hi = parseThreshold(branch.thresholdHigh);
        // An inverted (low > high) range never matches, same "never throw, just never match" contract
        // as an unparseable single threshold — surfaces as a coverage gap rather than silently
        // guessing which bound the teacher meant.
        if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi)
            return false;
        return value >= lo - EPS(lo) && value <= hi + EPS(hi);
    }
    const threshold = parseThreshold(branch.threshold);
    if (!Number.isFinite(threshold))
        return false;
    // Epsilon on the INCLUSIVE comparisons only, matching numerical's `containsValue`: an exclusive
    // boundary is an exact-equality question, and widening it re-admits the very point it exists to
    // exclude. Without this, an attainable value of 0.30000000000000004 — what walking a 0.1 step
    // actually produces — missed a teacher's `== 0.3` and was reported as an uncovered gap.
    switch (branch.operator) {
        case '<': return value < threshold;
        case '<=': return value <= threshold + EPS(threshold);
        case '==': return valuesMatchWithinEpsilon(value, threshold);
        case '>=': return value >= threshold - EPS(threshold);
        case '>': return value > threshold;
    }
}
/** Resolves which branch (if any) the shared equation currently matches — `null` both when
 * branching isn't enabled at all AND when it is but nothing resolved (missing/invalid equation, or
 * a coverage gap). Callers that need to tell those two apart (there's a real difference between
 * "not using this feature" and "using it, but broken right now") check `branches` themselves. */
export function resolveMatchedBranch(equation, branches, resolvedVariables, angleMode = 'degrees') {
    if (!branches || branches.length === 0 || !equation)
        return null;
    const value = evaluateEquation(equation, resolvedVariables, angleMode);
    if (value === null)
        return null;
    return branches.find(b => branchMatches(value, b)) ?? null;
}
function sameIdSet(a, b) {
    if (a.length !== b.length)
        return false;
    const setA = new Set(a);
    return b.every(id => setA.has(id));
}
/** Collapses a run of consecutive same-problem regions (all gaps, or all overlaps by the same
 * branch set) into one combined region — a standalone point keeps its own `kind: 'point'` region,
 * but a point sitting right next to a gap/overlap interval merges into that interval's inclusive
 * boundary instead of surfacing as its own separate one-value issue. */
function combineRegions(run) {
    if (run.length === 1)
        return run[0];
    const first = run[0];
    const last = run[run.length - 1];
    return {
        kind: 'interval',
        from: first.kind === 'point' ? first.value : first.from,
        fromInclusive: first.kind === 'point' ? true : undefined,
        to: last.kind === 'point' ? last.value : last.to,
        toInclusive: last.kind === 'point' ? true : undefined,
    };
}
/**
 * The threshold-derived region partition shared by `checkBranchCoverage` and
 * `getUnreachableBranchIds` — a `<`/`<=`/`==`/`>=`/`>` set of conditions carves the whole real
 * number line into a fixed sequence of regions bounded by the branches' own thresholds (no
 * operator's truth value can change except exactly at its own threshold), so sampling one point
 * per region — the threshold itself for a singleton `==` region, any interior point for the open
 * intervals between consecutive thresholds — is exact, not an approximation. This is why neither
 * caller ever needs to know the equation's variables at all: reachability/coverage are properties
 * of the conditions alone, so both work identically whether the equation references one variable
 * or five.
 */
function branchCoverageRegions(branches) {
    // A 'between' branch contributes TWO boundary points (its truth value can flip entering at the
    // low bound and again exiting at the high bound), not one — everything past this extraction is
    // unaware of which operator produced which threshold, since branchMatches (called per sampled
    // region below) is what actually knows how to test a 'between' branch against a point.
    const thresholds = Array.from(new Set(branches.flatMap(b => b.operator === 'between' ? [parseThreshold(b.threshold), parseThreshold(b.thresholdHigh)] : [parseThreshold(b.threshold)])
        .filter(t => Number.isFinite(t)))).sort((a, b) => a - b);
    const regions = [];
    if (thresholds.length === 0) {
        regions.push({ region: { kind: 'interval', from: null, to: null }, sample: 0 });
    }
    else {
        regions.push({ region: { kind: 'interval', from: null, to: thresholds[0] }, sample: thresholds[0] - 1 });
        for (let i = 0; i < thresholds.length; i++) {
            regions.push({ region: { kind: 'point', value: thresholds[i] }, sample: thresholds[i] });
            const next = thresholds[i + 1];
            if (next !== undefined) {
                regions.push({ region: { kind: 'interval', from: thresholds[i], to: next }, sample: (thresholds[i] + next) / 2 });
            }
        }
        regions.push({ region: { kind: 'interval', from: thresholds[thresholds.length - 1], to: null }, sample: thresholds[thresholds.length - 1] + 1 });
    }
    return regions;
}
/** Above this, treat the domain as continuous — enumerating is neither useful to a teacher (a
 * "missing 1, 2, 3, ... 4000" message helps nobody) nor worth the work. */
/**
 * Checks whether every possible result of the shared equation is matched by exactly one branch —
 * reasoning purely about each branch's own (operator, threshold) predicate, never the equation
 * itself. See branchCoverageRegions' doc comment for why sampling one point per region is exact.
 *
 * `domain`, when supplied (see `enumerateFormulaValues`), replaces the real line with the finite set
 * of values the equation can actually produce. Coverage over the continuum and coverage over that
 * set are genuinely different questions, and for a scenario-selector variable the continuum answer
 * is noise.
 */
export function checkBranchCoverage(branches, domain) {
    if (branches.length === 0)
        return { issues: [], unreachableBranchIds: [] };
    const withMatches = domain && domain.length > 0
        ? domain.map(value => ({
            region: { kind: 'point', value },
            matchingBranchIds: branches.filter(b => branchMatches(value, b)).map(b => b.id),
        }))
        : branchCoverageRegions(branches).map(({ region, sample }) => ({
            region,
            matchingBranchIds: branches.filter(b => branchMatches(sample, b)).map(b => b.id),
        }));
    // Merge consecutive regions that share the same coverage problem into one, so e.g. a `==0` gap
    // sitting right next to a `<0` gap reads as one "<=0" gap instead of two separate messages a
    // teacher has to mentally stitch back together — same idea for an overlap where the identical
    // branches win on both sides of a boundary.
    // Merging two adjacent DISCRETE points into "1 to 5" is only truthful when the domain has no
    // holes: over {1, 3, 5} that phrasing names 2 and 4 as problems, and neither can occur. So a
    // step-1 domain merges (the common scenario-selector case, where the interval is exact) and any
    // wider step reports each value on its own.
    /**
     * A branch matching nothing in the whole value set can never fire, so its answer setup is dead.
     * Derivable here and nowhere else: `issues` holds only the PROBLEM regions, while `withMatches`
     * is the complete table, and a branch is unreachable exactly when it appears in none of it.
     *
     * Only claimed over a known value set. Without a domain the regions are sampled from the
     * branches' own boundaries, so every branch matches its own sample by construction and the
     * question is meaningless rather than answered "no".
     */
    const unreachableBranchIds = domain && domain.length > 0
        ? branches
            .filter(b => !withMatches.some(w => w.matchingBranchIds.includes(b.id)))
            .map(b => b.id)
        : [];
    /**
     * Adjacent regions with the same problem merge, and over a domain "adjacent" means consecutive in
     * the ATTAINABLE SET, not one apart numerically. `withMatches` is built in domain order, so index
     * adjacency already is domain adjacency.
     *
     * This used to require a step of exactly 1, on the grounds that merging {1,3,5} into "1 to 5"
     * names 2 and 4 as problems when neither can occur. True, and it does not matter: an
     * over-inclusive GAP is harmless because the remedy is also correct — a condition spanning 1 to 5
     * covers every attainable value inside it. It is the opposite of the dangerous error, which would
     * be an over-inclusive claim of COVERAGE hiding a value a student can reach. Overlaps are purely
     * diagnostic and the same argument applies.
     *
     * Without the merge, `{n}*2` (step 2 in the attainable set) could not merge at all and reported
     * every uncovered value separately: "=2, =4, =6, =8" over a 1-10 variable, a hundred entries over
     * 1-100. Isolated gaps still stay itemised, since they have nothing adjacent to merge with, so
     * the discrete scenario-selector case keeps its precision.
     */
    const allowMerge = true;
    const issues = [];
    let i = 0;
    while (i < withMatches.length) {
        const cur = withMatches[i];
        if (cur.matchingBranchIds.length === 1) {
            i++;
            continue;
        }
        let j = i;
        while (allowMerge &&
            j + 1 < withMatches.length &&
            withMatches[j + 1].matchingBranchIds.length !== 1 &&
            sameIdSet(cur.matchingBranchIds, withMatches[j + 1].matchingBranchIds)) {
            j++;
        }
        issues.push({
            region: combineRegions(withMatches.slice(i, j + 1).map(r => r.region)),
            matchingBranchIds: cur.matchingBranchIds,
        });
        i = j + 1;
    }
    return { issues, unreachableBranchIds };
}
/**
 * Branch ids that `resolveMatchedBranch` (first-match-wins by array order — "Branches are checked
 * in order, first match wins," see ConditionalRoleBranch's own doc comment in dropdown.ts) can
 * NEVER actually select for any real equation value — fully shadowed by an earlier branch across
 * every sampled region (see branchCoverageRegions). A branch that's the sole matcher, or the
 * first-in-array-order among several matchers, for at least one region is reachable; one that's
 * always shadowed everywhere it would otherwise match is not — almost certainly a duplicate
 * condition, or one made redundant by editing/reordering an earlier branch, rather than deliberate
 * dead weight a teacher intended to keep. Analogous to Numerical's per-category
 * `mathErrorReachable`: the block computes its own "possible but not activated" states, and callers
 * (e.g. Flow) treat them as inactive rather than live options.
 */
export function getUnreachableBranchIds(branches) {
    if (branches.length === 0)
        return new Set();
    const reachable = new Set();
    for (const { sample } of branchCoverageRegions(branches)) {
        const winner = branches.find(b => branchMatches(sample, b));
        if (winner)
            reachable.add(winner.id);
    }
    return new Set(branches.filter(b => !reachable.has(b.id)).map(b => b.id));
}
/**
 * The feedback set an evaluation should actually consult, given whether a branch resolved.
 *
 * Three cases, and the third is the one that matters:
 *
 * 1. A branch matched — use that branch's own feedback.
 * 2. No branches configured at all — branching is off, so the block's top-level feedback IS the
 *    normal path and every state in it fires as authored.
 * 3. **Branches ARE configured and none matched** — incomplete coverage. Every option/value falls
 *    back to `neutral`, and this returns the top-level set with **every targeted state stripped**,
 *    so evaluation lands on Default Incorrect.
 *
 * Case 3 exists because the targeted lookups in each block are keyed on the ANSWER (option id,
 * word, order, value) and never consult its role — so a targeted state left over in the top-level
 * set fired terminally, ahead of Incorrect, for an answer that had just been declared neutral.
 * When that state was also blank the student got nothing at all: silent, and invisible to the
 * teacher, since the panel only ever shows the ACTIVE branch's states.
 *
 * Incomplete coverage is expected to become a publication-blocking error. This is the behaviour for
 * one that slips through anyway: always Incorrect, so a student can complain and a teacher can fix
 * it — never silence.
 *
 * Stripping the states here rather than gating each block's own targeted lookup is deliberate:
 * there are six such lookups and they are all shaped differently (one option, several words, an
 * order, a scale point), so a flag would have to be honoured correctly in six places. An empty
 * targeted set is honoured by all six for free.
 */
export function activeFeedbackForBranch(matchedBranchFeedback, topLevel, branchesConfigured) {
    if (matchedBranchFeedback)
        return matchedBranchFeedback;
    if (!branchesConfigured)
        return topLevel;
    return { ...topLevel, states: topLevel.states.filter(s => s.type !== 'targeted') };
}
/**
 * Branch ids provably unable to match anywhere in `[lo, hi]`, without knowing the attainable set.
 *
 * One-directional on purpose: it proves UNreachability and never claims reachability. A threshold
 * inside the bounds may still be unattainable — `{n}*2` skips every odd number — so "not proven
 * unreachable" is not "reachable". Where the full set is affordable, `checkBranchCoverage` answers
 * exactly and this is not used.
 *
 * Exists so the diagnosis survives a domain too large to enumerate: 1 to 100 in steps of 0.01 is
 * 9,901 values, and a condition of `= 0` is still obviously outside it.
 */
export function branchesUnreachableInRange(branches, lo, hi) {
    return branches.filter(b => {
        const t = parseThreshold(b.threshold);
        if (b.operator === 'between') {
            const t2 = parseThreshold(b.thresholdHigh);
            if (!Number.isFinite(t) || !Number.isFinite(t2))
                return false;
            return Math.max(t, t2) < lo || Math.min(t, t2) > hi;
        }
        if (!Number.isFinite(t))
            return false;
        switch (b.operator) {
            // Exclusive: needs a value strictly past the threshold, so touching the bound is not enough.
            case '<': return t <= lo;
            case '>': return t >= hi;
            case '<=': return t < lo;
            case '>=': return t > hi;
            case '==': return t < lo || t > hi;
            default: return false;
        }
    }).map(b => b.id);
}
//# sourceMappingURL=conditional-branch.js.map